import { motion } from "framer-motion";
import { createPortal } from "react-dom";

interface UploadProgressModalProps {
  visible: boolean;
  progress: number; // 0-100
  onCancel: () => void;
  currentFile?: number;
  totalFiles?: number;
  items?: UploadProgressItem[];
}

export type UploadProgressItemStatus =
  | "waiting"
  | "uploading"
  | "complete"
  | "failed"
  | "cancelled";

export interface UploadProgressItem {
  id: string;
  fileName: string;
  type?: "image" | "video";
  percent: number;
  status: UploadProgressItemStatus;
}

const statusLabel: Record<UploadProgressItemStatus, string> = {
  waiting: "대기",
  uploading: "업로드 중",
  complete: "완료",
  failed: "실패",
  cancelled: "취소",
};

/**
 * 업로드 진행률 모달
 * - 퍼센트 표시
 * - 프로그레스 바
 * - 취소 버튼
 * - Portal로 body에 직접 렌더링하여 z-index 문제 해결
 */
export function UploadProgressModal({
  visible,
  progress,
  onCancel,
  currentFile,
  totalFiles,
  items = [],
}: UploadProgressModalProps) {
  if (!visible) return null;

  // 진행률 0-100 범위로 제한
  const safeProgress = Math.min(100, Math.max(0, Math.round(progress)));
  const hasItemProgress = items.length > 0;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl p-6 mx-4 w-full max-w-sm shadow-xl"
      >
        {/* 제목 */}
        <h3 className="text-lg font-semibold text-center mb-4">
          업로드 중...
        </h3>

        {/* 파일 진행 상태 (여러 파일인 경우) */}
        {!hasItemProgress && totalFiles && totalFiles > 1 && (
          <p className="text-sm text-gray-500 text-center mb-2">
            {currentFile || 1} / {totalFiles} 파일
          </p>
        )}

        {/* 퍼센트 표시 */}
        <div className="text-3xl font-bold text-center text-orange-500 mb-4">
          {safeProgress}%
        </div>

        {/* 프로그레스 바 */}
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-6">
          <motion.div
            className="h-full bg-orange-400 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${safeProgress}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>

        {hasItemProgress && (
          <div className="max-h-52 overflow-y-auto space-y-3 mb-5">
            {items.map((item) => {
              const itemProgress = Math.min(100, Math.max(0, Math.round(item.percent || 0)));
              const isDone = item.status === "complete";
              const isFailed = item.status === "failed" || item.status === "cancelled";

              return (
                <div key={item.id} className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
                      {item.fileName}
                    </span>
                    <span
                      className={`shrink-0 text-xs font-medium ${
                        isDone
                          ? "text-green-600"
                          : isFailed
                            ? "text-red-500"
                            : "text-orange-500"
                      }`}
                    >
                      {statusLabel[item.status] || statusLabel.waiting}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <motion.div
                        className={`h-full rounded-full ${
                          isDone
                            ? "bg-green-500"
                            : isFailed
                              ? "bg-red-400"
                              : "bg-orange-400"
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${itemProgress}%` }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      />
                    </div>
                    <span className="w-9 text-right text-xs tabular-nums text-gray-500">
                      {itemProgress}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 안내 문구 */}
        <p className="text-xs text-gray-400 text-center mb-4">
          업로드가 완료될 때까지 앱을 종료하지 마세요
        </p>

        {/* 취소 버튼 */}
        <button
          onClick={onCancel}
          className="w-full py-3 text-gray-600 bg-gray-100 rounded-full font-medium hover:bg-gray-200 transition-colors"
        >
          취소
        </button>
      </motion.div>
    </div>
  );

  // Portal로 body에 직접 렌더링하여 z-index 문제 해결
  return createPortal(modalContent, document.body);
}
